import { OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import {
  getConsentFormStatus,
  isConsentQuestionnaire
} from 'src/app/core/api/consent-form.util';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { IntakeForm, QuestionnaireRequest } from './../../../API';

@Pipe({
  name: 'questionnaireScore',
  pure: false
})
export class QuestionnaireScorePipe implements PipeTransform, OnDestroy {
  private translations: Record<string, string> = {};
  private translationSub: Subscription;
  private lastTransformResult: string = '';
  private lastCacheKey: string = null;

  constructor(
    private questionnaireRequestService: QuestionnaireRequestService,
    private translocoService: TranslocoService
  ) {
    this.translationSub = this.translocoService
      .selectTranslateObject('QUESTIONNAIRE_RESPONSE')
      .subscribe(map => {
        this.translations = map;
        this.lastTransformResult = '';
      });
  }

  transform(
    questionnaire: Partial<QuestionnaireRequest> | Partial<IntakeForm>,
    showDisclaimer: boolean = false,
    disclaimerHtml: string = ''
  ) {
    const isConsent = isConsentQuestionnaire(questionnaire);
    const consentStatus = isConsent ? getConsentFormStatus(questionnaire) : null;
    const cacheKey = JSON.stringify({
      scores:
        questionnaire && questionnaire.response ? questionnaire.response['scores'] : null,
      type: questionnaire ? questionnaire['type'] : null,
      consentStatus,
      showDisclaimer,
      disclaimerHtml
    });

    if (this.lastTransformResult && this.lastCacheKey === cacheKey) {
      return this.lastTransformResult;
    }
    this.lastCacheKey = cacheKey;

    if (!this.translations || !this.translations['atRisk']) {
      this.lastTransformResult = 'N/A';
      return this.lastTransformResult;
    }

    if (isConsent) {
      this.lastTransformResult = this.getConsentScoreHtml(consentStatus);
      return this.lastTransformResult;
    }

    if (!(questionnaire && questionnaire.response && questionnaire.response['scores'])) {
      this.lastTransformResult = this.translocoService.translate('N/A');
      return this.lastTransformResult;
    }

    if (showDisclaimer && !disclaimerHtml) {
      disclaimerHtml = `<div class="disclaimer">${this.translations['scoreDisclaimer']}</div>`;
    }

    const scores =
      typeof questionnaire.response['scores'] === 'string'
        ? questionnaire.response['scores']
        : JSON.stringify(questionnaire.response['scores']);

    const scoreJSON = JSON.parse(scores);

    if (
      Object.keys(scoreJSON).length === 0 ||
      scoreJSON['CUSTOM'] === 'N/A' ||
      questionnaire['type'] === 'DERFS' ||
      questionnaire['type'] === 'CDERFS'
    ) {
      this.lastTransformResult = this.translocoService.translate('N/A');
      return this.lastTransformResult;
    }

    const riskKey = this.questionnaireRequestService.isAtRisk(scores) ? 'atRisk' : 'notAtRisk';
    const riskClass = riskKey === 'atRisk' ? 'at-risk' : 'not-at-risk';
    const riskText = this.translations[riskKey];

    const isAtRisk =
      `<span class="${riskClass}"><b>${riskText}</b></span>` +
      (showDisclaimer ? disclaimerHtml : '');

    let res = '';
    Object.keys(scoreJSON).forEach(key => {
      const friendlyName =
        this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap[key] || key;
      res =
        res +
        (res !== '' ? ' | ' : '') +
        `<b>${this.translations[friendlyName] || friendlyName}:</b> ${scoreJSON[key]}`;
      const category = this.questionnaireRequestService.getCategorizedScore(key, scoreJSON[key]);
      if (category) {
        res += ` (${this.translocoService.translate(category)})`;
      }
    });

    this.lastTransformResult = res + '<br>' + isAtRisk;
    return this.lastTransformResult;
  }

  private getConsentScoreHtml(consentStatus: 'accepted' | 'rejected' | null): string {
    if (consentStatus === 'accepted') {
      const label =
        this.translations['consentAccepted'] ||
        this.translocoService.translate('QUESTIONNAIRE_RESPONSE.consentAccepted');
      return `<span class="not-at-risk"><b>${label}</b></span>`;
    }
    if (consentStatus === 'rejected') {
      const label =
        this.translations['consentDeclined'] ||
        this.translocoService.translate('QUESTIONNAIRE_RESPONSE.consentDeclined');
      return `<span class="at-risk"><b>${label}</b></span>`;
    }
    return this.translocoService.translate('N/A');
  }

  ngOnDestroy(): void {
    this.translationSub.unsubscribe();
  }
}
